import axios from 'axios';
import * as Constants from './constants';
import authHeader from './auth-header';
import { getPrivacySettings } from './privacy.service';
import {
  markPrivateStayEventsUploaded,
  prunePrivateStayEvents,
} from './location-tracking.service';

function retentionHours(settings) {
  const value = Number(settings.retainRawLocationHours);

  if (!Number.isFinite(value) || value <= 0) {
    return 24;
  }

  return Math.min(value, 24 * 90);
}

export async function uploadPendingStayEvents() {
  const settings = getPrivacySettings();
  const hours = retentionHours(settings);

  const stays = prunePrivateStayEvents(hours);

  if (!settings.shareExposureStays) {
    return {
      uploaded: 0,
      pending: stays.filter(x => !x.uploaded).length,
      reason: 'sharing_disabled',
    };
  }

  const headers = authHeader();

  if (!headers.Authorization) {
    return {
      uploaded: 0,
      pending: stays.filter(x => !x.uploaded).length,
      reason: 'not_authenticated',
    };
  }

  const now = Date.now();

  const pending = stays.filter(stay => {
    if (stay.uploaded) return false;

    const ended = new Date(stay.endedAt).getTime();

    if (!Number.isFinite(ended)) return false;

    const retentionUntil =
      ended + hours * 60 * 60 * 1000;

    return retentionUntil > now;
  });

  if (!pending.length) {
    return {
      uploaded: 0,
      pending: 0,
      reason: 'nothing_pending',
    };
  }

  const events = pending.map(stay => ({
    id: stay.id,
    latitude: stay.latitude,
    longitude: stay.longitude,
    accuracy: stay.accuracy ?? null,
    startedAt: stay.startedAt,
    endedAt: stay.endedAt,
    source: stay.source || 'automatic_foreground',

    // Retention is anchored to when the stay ended.
    // A delayed upload never extends raw-location retention.
    retentionUntil: new Date(
      new Date(stay.endedAt).getTime() +
      hours * 60 * 60 * 1000
    ).toISOString(),
  }));

  const response = await axios.post(
    Constants.API_URL + 'stayEvents/batch',
    { events },
    { headers }
  );

  const accepted =
    response &&
    response.data &&
    Array.isArray(response.data.acceptedClientEventIds)
      ? response.data.acceptedClientEventIds
      : [];

  markPrivateStayEventsUploaded(accepted);

  return {
    uploaded: accepted.length,
    acceptedClientEventIds:
      accepted.map(String),
    inserted:
      response && response.data
        ? Number(response.data.inserted || 0)
        : 0,
    rejected:
      response && response.data
        ? Number(response.data.rejected || 0)
        : 0,
    pending: Math.max(0, pending.length - accepted.length),
    reason: 'ok',
  };
}
